export const WS_REALTIME_TOPIC_EVENT = "wm-topic";

export const TOPIC_OPERATOR_ASYNC_OPERATION_UPDATE = "operator.async_operation.update";
export const TOPIC_PARTICIPANT_BROADCAST_UPDATE = "participant.broadcast.update";
export const TOPIC_PARTICIPANT_JOURNEY_REFRESH = "participant.journey.refresh";
export const TOPIC_EVENT_REFRESH_TAGS_UPDATED = "event.refresh_tags.updated";
export const TOPIC_USER_REFRESH_TAGS_UPDATED = "user.refresh_tags.updated";
export const TOPIC_REFRESH_TAGS_INVALIDATE = "refresh.tags.invalidate";

export type RealtimeTopicScope = {
  kind: "event" | "user";
  channel?: string;
  event_id?: string;
  user_id?: string;
};

export type RealtimeTopicEnvelope<TPayload = Record<string, unknown>> = {
  id: string;
  topic: string;
  occurred_at: string;
  schema_version: number;
  scope: RealtimeTopicScope;
  payload: TPayload;
};

export type ParticipantBroadcastUpdatePayload = {
  id?: string;
  broadcast_id?: string;
  type?: string;
  title?: string;
  body?: string;
  icon?: string;
  action_url?: string;
  sent_at?: string;
  persistent?: boolean;
};

export type ParticipantJourneyRefreshPayload = {
  participant_id: string;
  event_id: string;
  reason?: string;
  schema_version?: number;
  updated_at?: string;
};

export type OperatorAsyncOperationPayload = {
  operation_id: string;
  operation?: string;
  status: "pending" | "running" | "succeeded" | "failed" | "cancelled";
  message?: string;
  status_url?: string;
  result_url?: string;
  refresh?: string[];
  error?: string;
};

export type OperatorAsyncOperationEnvelope = RealtimeTopicEnvelope<OperatorAsyncOperationPayload>;

export type RefreshTagVersion = {
  tag: string;
  version: number;
};

export type RefreshTagsUpdatedPayload = {
  subject_key: string;
  reason?: string;
  versions: RefreshTagVersion[];
  updated_at?: string;
};

export type RefreshTagInvalidationPayload = {
  tags: string[];
  reason?: string;
  source?: string;
  updated_at?: string;
};

export type ParticipantBroadcastUpdateEnvelope = RealtimeTopicEnvelope<ParticipantBroadcastUpdatePayload>;
export type ParticipantJourneyRefreshEnvelope = RealtimeTopicEnvelope<ParticipantJourneyRefreshPayload>;
export type RefreshTagsUpdatedEnvelope = RealtimeTopicEnvelope<RefreshTagsUpdatedPayload>;
export type RefreshTagInvalidationEnvelope = RealtimeTopicEnvelope<RefreshTagInvalidationPayload>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function decodeRealtimeTopicEnvelope(raw: unknown): RealtimeTopicEnvelope | null {
  if (!isRecord(raw)) return null;
  const id = String(raw.id || "").trim();
  const topic = String(raw.topic || "").trim();
  const occurredAt = String(raw.occurred_at || "").trim();
  const schemaVersion = Number(raw.schema_version);
  const scope = raw.scope;
  const payload = raw.payload;
  if (!id || !topic || !occurredAt || !Number.isFinite(schemaVersion) || !isRecord(scope) || !isRecord(payload)) {
    return null;
  }
  const kind = String(scope.kind || "").trim();
  if (kind !== "user" && kind !== "event") {
    return null;
  }
  return {
    id,
    topic,
    occurred_at: occurredAt,
    schema_version: schemaVersion,
    scope: {
      kind: kind === "event" ? "event" : "user",
      channel: String(scope.channel || "").trim() || undefined,
      event_id: String(scope.event_id || "").trim() || undefined,
      user_id: String(scope.user_id || "").trim() || undefined,
    },
    payload,
  };
}

export function isParticipantBroadcastUpdateEnvelope(
  envelope: RealtimeTopicEnvelope | null,
): envelope is ParticipantBroadcastUpdateEnvelope {
  return envelope?.topic === TOPIC_PARTICIPANT_BROADCAST_UPDATE;
}

export function isOperatorAsyncOperationEnvelope(
  envelope: RealtimeTopicEnvelope | null,
): envelope is OperatorAsyncOperationEnvelope {
  return envelope?.topic === TOPIC_OPERATOR_ASYNC_OPERATION_UPDATE;
}

export function isParticipantJourneyRefreshEnvelope(
  envelope: RealtimeTopicEnvelope | null,
): envelope is ParticipantJourneyRefreshEnvelope {
  return envelope?.topic === TOPIC_PARTICIPANT_JOURNEY_REFRESH;
}

export function isRefreshTagsUpdatedEnvelope(
  envelope: RealtimeTopicEnvelope | null,
): envelope is RefreshTagsUpdatedEnvelope {
  return envelope?.topic === TOPIC_EVENT_REFRESH_TAGS_UPDATED || envelope?.topic === TOPIC_USER_REFRESH_TAGS_UPDATED;
}

export function isRefreshTagInvalidationEnvelope(
  envelope: RealtimeTopicEnvelope | null,
): envelope is RefreshTagInvalidationEnvelope {
  return envelope?.topic === TOPIC_REFRESH_TAGS_INVALIDATE;
}
